export interface DeepgramLanguage {
  label: string;
  code: string;
}

/** Nova-3 supported languages (see Deepgram models-languages-overview). */
export const DEEPGRAM_LANGUAGES: DeepgramLanguage[] = [
  { label: "Auto / Multilingual", code: "multi" },
  { label: "English", code: "en" },
  { label: "English (US)", code: "en-US" },
  { label: "English (Australia)", code: "en-AU" },
  { label: "English (UK)", code: "en-GB" },
  { label: "English (India)", code: "en-IN" },
  { label: "English (New Zealand)", code: "en-NZ" },
  { label: "Arabic", code: "ar" },
  { label: "Arabic (UAE)", code: "ar-AE" },
  { label: "Arabic (Saudi Arabia)", code: "ar-SA" },
  { label: "Arabic (Qatar)", code: "ar-QA" },
  { label: "Arabic (Kuwait)", code: "ar-KW" },
  { label: "Arabic (Syria)", code: "ar-SY" },
  { label: "Arabic (Lebanon)", code: "ar-LB" },
  { label: "Arabic (Palestine)", code: "ar-PS" },
  { label: "Arabic (Jordan)", code: "ar-JO" },
  { label: "Arabic (Egypt)", code: "ar-EG" },
  { label: "Arabic (Sudan)", code: "ar-SD" },
  { label: "Arabic (Chad)", code: "ar-TD" },
  { label: "Arabic (Morocco)", code: "ar-MA" },
  { label: "Arabic (Algeria)", code: "ar-DZ" },
  { label: "Arabic (Tunisia)", code: "ar-TN" },
  { label: "Arabic (Iraq)", code: "ar-IQ" },
  { label: "Arabic (Iran)", code: "ar-IR" },
  { label: "Belarusian", code: "be" },
  { label: "Bengali", code: "bn" },
  { label: "Bosnian", code: "bs" },
  { label: "Bulgarian", code: "bg" },
  { label: "Catalan", code: "ca" },
  { label: "Chinese (Mandarin, Simplified)", code: "zh-CN" },
  { label: "Chinese (Mandarin, Simplified, alt)", code: "zh" },
  { label: "Chinese (Mandarin, Simplified, Hans)", code: "zh-Hans" },
  { label: "Chinese (Cantonese, Hong Kong)", code: "zh-HK" },
  { label: "Chinese (Mandarin, Traditional, Taiwan)", code: "zh-TW" },
  { label: "Chinese (Mandarin, Traditional, Hant)", code: "zh-Hant" },
  { label: "Croatian", code: "hr" },
  { label: "Czech", code: "cs" },
  { label: "Danish", code: "da" },
  { label: "Danish (Denmark)", code: "da-DK" },
  { label: "Dutch", code: "nl" },
  { label: "Flemish", code: "nl-BE" },
  { label: "Estonian", code: "et" },
  { label: "Finnish", code: "fi" },
  { label: "French", code: "fr" },
  { label: "French (Canada)", code: "fr-CA" },
  { label: "German", code: "de" },
  { label: "German (Switzerland)", code: "de-CH" },
  { label: "Greek", code: "el" },
  { label: "Gujarati", code: "gu" },
  { label: "Gujarati (India)", code: "gu-IN" },
  { label: "Hebrew", code: "he" },
  { label: "Hindi", code: "hi" },
  { label: "Hungarian", code: "hu" },
  { label: "Indonesian", code: "id" },
  { label: "Italian", code: "it" },
  { label: "Japanese", code: "ja" },
  { label: "Kannada", code: "kn" },
  { label: "Korean", code: "ko" },
  { label: "Korean (South Korea)", code: "ko-KR" },
  { label: "Latvian", code: "lv" },
  { label: "Lithuanian", code: "lt" },
  { label: "Macedonian", code: "mk" },
  { label: "Malay", code: "ms" },
  { label: "Marathi", code: "mr" },
  { label: "Norwegian", code: "no" },
  { label: "Persian", code: "fa" },
  { label: "Polish", code: "pl" },
  { label: "Portuguese", code: "pt" },
  { label: "Portuguese (Brazil)", code: "pt-BR" },
  { label: "Portuguese (Portugal)", code: "pt-PT" },
  { label: "Romanian", code: "ro" },
  { label: "Russian", code: "ru" },
  { label: "Serbian", code: "sr" },
  { label: "Slovak", code: "sk" },
  { label: "Slovenian", code: "sl" },
  { label: "Spanish", code: "es" },
  { label: "Spanish (Latin America)", code: "es-419" },
  { label: "Swedish", code: "sv" },
  { label: "Swedish (Sweden)", code: "sv-SE" },
  { label: "Tagalog", code: "tl" },
  { label: "Tamil", code: "ta" },
  { label: "Telugu", code: "te" },
  { label: "Thai", code: "th" },
  { label: "Thai (Thailand)", code: "th-TH" },
  { label: "Turkish", code: "tr" },
  { label: "Ukrainian", code: "uk" },
  { label: "Urdu", code: "ur" },
  { label: "Vietnamese", code: "vi" },
];

/**
 * Recording defaults to English rather than "Auto / Multilingual".
 *
 * Auto-detection is not reliable enough in practice to be the default a
 * clinician silently gets; most consultations are in English, and picking a
 * concrete language up front produces better transcripts. Auto stays in the
 * dropdown for anyone who wants it. Resolved by code, not list position, so
 * reordering the list cannot silently change the default.
 */
export const DEFAULT_DEEPGRAM_LANGUAGE: DeepgramLanguage =
  DEEPGRAM_LANGUAGES.find((language) => language.code === "en") ??
  DEEPGRAM_LANGUAGES[0];

const CODE_INDEX = new Map(
  DEEPGRAM_LANGUAGES.map((lang) => [lang.code, lang] as const)
);

export function findDeepgramLanguageByCode(
  code: string | undefined | null
): DeepgramLanguage | undefined {
  if (!code) return undefined;
  return CODE_INDEX.get(code);
}

export function isValidDeepgramLanguageCode(code: string): boolean {
  return CODE_INDEX.has(code);
}
